import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-20" data-testid="privacy-page">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Legal</div>
      <h1 className="font-display text-4xl md:text-6xl text-white mt-2">Privacy Policy</h1>
      <p className="text-white/60 mt-3 text-sm">Last updated: {new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}</p>

      <div className="ufr-card rounded-md p-6 md:p-10 mt-10 space-y-8 text-white/75 leading-relaxed text-sm md:text-base">

        <Section title="1. Who we are">
          <p>Ultimate Football Raffles ("UFR", "we", "us", "our") operates ultimatefootballraffles.com, a skill-based prize competition platform based in the United Kingdom. This Privacy Policy explains how we collect, use, store and protect your personal data, and your rights under the <strong>UK General Data Protection Regulation (UK GDPR)</strong> and the <strong>Data Protection Act 2018</strong>.</p>
          <p>For all data-protection matters, our controller and contact point is: Ultimate Football Raffles, First Floor, Unit L, Alma Park, Wibtoft, Leicestershire, LE17 5BH, United Kingdom — <a href="mailto:Support@ultimatefootballraffles.com" className="text-[color:var(--ufr-green)]">Support@ultimatefootballraffles.com</a>.</p>
        </Section>

        <Section title="2. What data we collect">
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Account data</strong> — your name, email address and encrypted password.</li>
            <li><strong>Entry data</strong> — the competitions you enter, tickets purchased, ticket numbers, trivia answers and postal entries.</li>
            <li><strong>Payment data</strong> — processed securely by our third-party payment provider. We do <strong>not</strong> store or have access to your full card number, CVV or expiry date. We only retain a transaction reference and status.</li>
            <li><strong>Technical data</strong> — IP address, browser type, device information and cookies (see section 8).</li>
            <li><strong>Communications</strong> — any correspondence you send to us (e.g. support emails).</li>
          </ul>
        </Section>

        <Section title="3. How and why we use your data (lawful bases)">
          <p>Under UK GDPR we only process your personal data where we have a lawful basis to do so:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Performance of a contract</strong> — to create and manage your account, process entries, take payment, run draws and deliver prizes.</li>
            <li><strong>Legal obligation</strong> — to comply with accounting, tax, anti-fraud and consumer-protection laws.</li>
            <li><strong>Legitimate interests</strong> — to secure our platform, prevent fraud and chargeback abuse, and improve our service. We balance these interests against your rights.</li>
            <li><strong>Consent</strong> — to send you marketing about future competitions (where you opt in) and for non-essential cookies. You can withdraw consent at any time.</li>
          </ul>
        </Section>

        <Section title="4. Payments, PCI-DSS and card security">
          <p>All card payments are handled by an FCA-regulated, <strong>PCI-DSS compliant</strong> third-party payment provider. Card details are entered on the provider's secure infrastructure and are never transmitted to or stored on UFR's servers. Transactions are protected by TLS encryption and, where offered by your bank, 3-D Secure (Strong Customer Authentication under the Payment Services Regulations 2017). UFR retains only a tokenised reference so we can reconcile and, where necessary, refund your payment.</p>
        </Section>

        <Section title="5. Who we share your data with">
          <p>We never sell your personal data. We only share it with:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Payment providers</strong> — to process transactions and refunds.</li>
            <li><strong>Email service providers</strong> — to send transactional emails (entry confirmations, winner notifications) and, where you opt in, marketing.</li>
            <li><strong>Analytics providers</strong> — aggregated, where enabled, to understand site usage.</li>
            <li><strong>Delivery partners</strong> — the name and address of winners, solely to dispatch prizes.</li>
            <li><strong>Authorities</strong> — where required by law, regulation or a valid court order.</li>
          </ul>
        </Section>

        <Section title="6. International transfers">
          <p>Some of our service providers may process data outside the UK/EEA. Where they do, we ensure an adequate level of protection through UK adequacy regulations, the UK International Data Transfer Agreement (IDTA), or Standard Contractual Clauses with additional safeguards.</p>
        </Section>

        <Section title="7. How long we keep your data">
          <p>We keep account and entry data for as long as your account is active and for a reasonable period afterwards. Transaction and financial records are retained for at least <strong>6 years</strong> to meet UK tax and accounting obligations. When data is no longer needed, it is securely deleted or anonymised.</p>
        </Section>

        <Section title="8. Cookies">
          <p>We use essential cookies to keep you logged in and to secure your session. With your consent, we may also use analytics cookies to measure traffic and improve the site. You can manage or block cookies through your browser settings; blocking essential cookies may affect how the site works.</p>
        </Section>

        <Section title="9. Your rights under UK GDPR">
          <p>You have the right to:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Be informed about how your data is used (this policy);</li>
            <li>Access a copy of the personal data we hold about you;</li>
            <li>Rectify inaccurate or incomplete data;</li>
            <li>Request erasure ("right to be forgotten"), subject to our legal retention duties;</li>
            <li>Restrict or object to processing, including opting out of marketing at any time;</li>
            <li>Data portability — receive your data in a structured, machine-readable format;</li>
            <li>Withdraw consent where processing is based on consent.</li>
          </ul>
          <p>To exercise any of these rights, email <a href="mailto:Support@ultimatefootballraffles.com" className="text-[color:var(--ufr-green)]">Support@ultimatefootballraffles.com</a>. We will respond within one month.</p>
        </Section>

        <Section title="10. Data security">
          <p>We protect your data with encryption in transit (TLS), hashed passwords, access controls and regular security reviews. No system is completely secure, but we take reasonable technical and organisational measures to safeguard your information, and we will notify you and the ICO of any breach where legally required.</p>
        </Section>

        <Section title="11. Children">
          <p>UFR is strictly for adults aged 18 and over. We do not knowingly collect data from anyone under 18. If we learn that we have, we will delete it promptly.</p>
        </Section>

        <Section title="12. Complaints">
          <p>If you have concerns about how we handle your data, please contact us first at <a href="mailto:Support@ultimatefootballraffles.com" className="text-[color:var(--ufr-green)]">Support@ultimatefootballraffles.com</a>. You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at <a href="https://ico.org.uk/" target="_blank" rel="noreferrer" className="text-[color:var(--ufr-green)]">ico.org.uk</a>.</p>
        </Section>

        <Section title="13. Changes to this policy">
          <p>We may update this Privacy Policy from time to time. The "last updated" date at the top reflects the latest version. Material changes will be notified by email to registered users where reasonably practical.</p>
        </Section>
      </div>

      <div className="mt-10 text-center">
        <Link to="/competitions" className="btn-ufr-primary inline-block px-8 py-4 text-sm">Back to raffles</Link>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-xl md:text-2xl text-white mb-2">{title}</h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
